import { forwardRef, FunctionComponent, PropsWithoutRef, Ref, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { BaseProps, ForwardProps, PropsWithDefaults } from '../../types';
import { defaultThemeProp } from '../../theme';
import { calculateFontSize } from '../../styles';

export interface FormControlProps extends BaseProps {
  /**
   * Sets DOM id for the control and associates label element via 'for' attribute.
   * If an id is not pass, a random id will be generated for any render.
   */
  id?: string;
  /** Set visual state based on a validation state. */
  status?: 'success' | 'warning' | 'error';
  /** Pass a string or a fragment with an Icon and string. */
  label?: ReactNode;
  /** Visually hides the label region. */
  labelHidden?: boolean;
  /** It is recommended to pass a simple string to offer guidance. Text will be styled based on status prop. */
  info?: ReactNode;
  /** Indicate if the field is required. The browser defaults to false. */
  required?: boolean;
  /** Placeholder text. The browser defaults to an empty string. */
  placeholder?: string;
  /**
   * Creates a controlled input and sets the value. Requires an onChange handler to update value.
   * value + onChange is the recommended method per React team.
   */
  value?: string;
  /**
   * If you wish to use an [uncontrolled input](https://reactjs.org/docs/uncontrolled-components.html), pass a defaultValue prop and a ref to access the input's native value prop or by other DOM ref means.
   */
  defaultValue?: string;
  /** Disable the control. The browser defaults to false. */
  disabled?: boolean;
  /** Makes the input non editable and non clickable. The browser defaults to false. */
  readOnly?: boolean;
  /** Sets html name attribute for the underlying control. Useful for mapping to a data field. */
  name?: string;
}

type FormControlPropsWithDefaults = PropsWithDefaults<FormControlProps>;

export const StyledFormControl = styled.div<FormControlPropsWithDefaults>(props => {
  const {
    theme: {
      base: { 'border-radius': baseRadius, 'font-size': baseFontSize, 'font-scale': baseFontScale },
      components: {
        'form-field': formField,
        'form-control': {
          'foreground-color': foreground,
          'background-color': background,
          'border-color': brd,
          'border-radius': radius,
          'border-width': borderWidth,
          ':hover': { 'border-color': hoverBorderColor },
          ':focus': { 'border-color': focusBorderColor, 'box-shadow': shadow },
          ':disabled': {
            'border-color': disabledBorderColor,
            'background-color': disabledBackgroundColor
          },
          ':read-only': {
            'border-color': readOnlyBorderColor,
            'background-color': readOnlyBackgroundColor
          }
        }
      }
    },
    status
  } = props;
  const fontSize = calculateFontSize(baseFontSize, baseFontScale);

  const borderColor = status && formField[status] ? formField[status]['status-color'] : brd;

  return css`
    color: ${foreground};
    background-color: ${background};
    border-radius: calc(${baseRadius} * ${radius});
    border-color: ${borderColor};
    border-width: ${borderWidth};
    border-style: solid;
    &,
    & > select {
      outline: none;
    }

    &:disabled,
    &[disabled] {
      background-color: ${disabledBackgroundColor};
      border-color: ${disabledBorderColor};
      cursor: not-allowed;
    }

    &:focus:not([disabled]) {
      border-color: ${focusBorderColor};
      box-shadow: ${shadow};
    }

    ${!status &&
    css`
      &:hover:not([readonly]):not([disabled]):not(:focus) {
        border-color: ${hoverBorderColor};
      }
    `}

    &[readonly] {
      background-color: ${readOnlyBackgroundColor};
      border-color: ${readOnlyBorderColor};
    }

    @media (pointer: coarse) {
      /* stylelint-disable-next-line unit-allowed-list */
      font-size: max(${fontSize.s}, 16px);
    }
  `;
});

StyledFormControl.defaultProps = defaultThemeProp;

const FormControl: FunctionComponent<FormControlProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<FormControlProps>, ref: Ref<HTMLDivElement>) => {
    return <StyledFormControl ref={ref} {...props} />;
  }
);

export default FormControl;
